import { Offer, Option } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';
import { SelectionCategory, LocalisedValue } from 'modules/shared-menu/types';

import { CategoryLine, OfferLine, OptionLine } from './types';
import { ITEM_MAX_IMAGE_COUNT } from '../../constants';

export type ShapelessLine = Record<string, string | number | null | undefined>;
export type ShapedLine = OfferLine | OptionLine | CategoryLine;

export type FieldOf<Line extends ShapedLine> = Line extends OfferLine
  ? keyof OfferLine
  : Line extends OptionLine
    ? keyof OptionLine
    : Line extends CategoryLine
      ? keyof CategoryLine
      : never;

export type LineSpec<T extends OfferLine | OptionLine | CategoryLine> = {
  fields: FieldOf<T>[];
  numericFields: Partial<FieldOf<T>>[];
  booleanFields: Partial<FieldOf<T>>[];
  enforceStringFields: Partial<FieldOf<T>>[];
  enforceMaxImagesFields?: Partial<FieldOf<T>>[];
};

export const categoryModelToCategoryLine = (category: SelectionCategory, lang: string): Partial<CategoryLine> => ({
  id: category.id,
  name: unlocalisedString(category.name, lang),
  description: unlocalisedString(category.description, lang),
  subcategories: category.subcategories?.map(category => category.id).join(',\n'),
  offers: category.offer_bindings?.map(offer => offer.offer_id).join(',\n'),
});

export const offerModelToOfferLine = (offer: Offer, lang: string): Partial<OfferLine> => ({
  id: offer.id,
  name: unlocalisedString(offer.name, lang),
  additives: unlocalisedString(offer.additives, lang),
  alcohol_percentage: offer.alcohol_percentage.toString(),
  allergens: unlocalisedString(offer.allergens, lang),
  brand_id: offer.brand_id,
  caffeine_content_units: offer.caffeine_content?.units,
  caffeine_content_value: offer.caffeine_content?.value,
  conditions_of_use_and_storage: unlocalisedString(offer.conditions_of_use_and_storage, lang),
  country_of_origin: unlocalisedString(offer.country_of_origin, lang),
  courier_restrictions: offer.courier_restrictions?.join(',\n'),
  delivery_methods: offer.delivery_methods?.join(',\n'),
  deposit_id: offer.deposit_permutation_id,
  external_id: offer.external_id,
  is_bundle_offer: offer.is_bundle_offer,
  max_quantity_per_purchase: offer.max_quantity_per_purchase,
  min_quantity_per_purchase: offer.min_quantity_per_purchase,
  merchant_sku: offer.merchant_sku,
  number_of_units: offer.number_of_units,
  price: offerOriginalPrice(offer),
  volume_in_ml: offer.volume_in_ml,
  weight_in_grams: offer.weight_in_grams,
  wolt_plus_only: offer.wolt_plus_only,
  images: offer.images?.map(image => image.url).join(',\n'),
  description: unlocalisedString(offer.description, lang),
  dietary_preferences: offer.dietary_preferences?.join(',\n'),
  discount_type: offer.discounted?.discount_type,
  discount: discountAmount(offer),
  display_unit: offer.display_unit,
  distributor_information: unlocalisedString(offer.distributor_information, lang),
  enabled: offer.enabled.enabled,
  // after: offer.enabled.after,
  // before: offer.enabled.before,
  gtin: offer.barcode_gtin,
  ingredients: unlocalisedString(offer.ingredients, lang),
  inventory_mode: offer.inventory_mode,
  mandatory_warnings: offer.mandatory_warnings?.join(',\n'),
  more_information: unlocalisedString(offer.more_information, lang),
  nutrition_facts: unlocalisedString(offer.nutrition_facts, lang),
  option_bindings: offer.option_bindings?.map(binding => binding.option_id).join(',\n'),
  producer_information: unlocalisedString(offer.producer_information, lang),

  sell_by_weight_type: offer.sell_by_weight_config?.input_type,
  sell_by_weight_grams_per_step: offer.sell_by_weight_config?.grams_per_step,
  user_requirements: offer.user_requirements?.join(',\n'),
  vat: offer.vat_percentage,
  vat_category: offer.vat_category_code,
  product_hierarchy_level_id: offer.product_hierarchy_level_id,
  // weekly_availability_start: offer?.weekly_availability?.[0]?.toString(),
  // weekly_availability_end: offer?.weekly_availability?.[1]?.toString(),
  // weekly_visibility_start: offer.weekly_visibility?.[0]?.toString(),
  // weekly_visibility_end: offer.weekly_visibility?.[1]?.toString(),

  // restrictions: offer.restrictions @todo handle this
  // nutrition_values: unlocalisedString(offer.nutrition_values, lang) @todo handle this
});

export const optionModelToOptionLines = (option: Option, lang: string): Partial<OptionLine>[] => {
  const defaultIndex = option.default_value_index;
  const unlocalised = (values: LocalisedValue[] | undefined) => unlocalisedString(values, lang);
  const optionHead = {
    id: option.id,
    name: option.name?.find(value => value.lang === lang)?.value,
    type: option.type,
  };

  const lines: Partial<OptionLine>[] = option.values.map((optionValue, index) => ({
    option_value_id: optionValue.id,
    deposit_id: optionValue.deposit_permutation_id,
    default: defaultIndex === index,
    external_id: optionValue.external_id ?? undefined,
    price: optionValue.price.toString(), // @todo change this
    vat: optionValue.vat_percentage?.toString(),
    vat_category: optionValue.vat_category_code ?? undefined,
    visible_to_courier: optionValue.visible_to_courier,
    visible_to_merchant_app: optionValue.visible_to_merchant_app,

    min: optionValue.multi_choice_config?.selection_range.min,
    max: optionValue.multi_choice_config?.selection_range.max,

    // field to be joined
    mandatory_warnings: optionValue.mandatory_warnings?.join(',\n'),
    dietary_preferences: optionValue.dietary_preferences?.join(',\n'),

    // I18n string to simple string
    additives: unlocalised(optionValue.additives),
    allergens: unlocalised(optionValue.allergens),
    ingredients: unlocalised(optionValue.ingredients),
    option_value_name: unlocalised(optionValue.name),
    nutrition_facts: unlocalised(optionValue.nutrition_facts),
    producer_information: unlocalised(optionValue.producer_information),
    distributor_information: unlocalised(optionValue.distributor_information),
  }));
  return [{ ...optionHead, ...lines[0] }, ...lines.slice(1)];
};

export const optionModelsToOptionLines = (options: Option[], lang: string): Partial<OptionLine>[] =>
  options.flatMap(option => optionModelToOptionLines(option, lang).concat({}));

export const decoder = (currency?: string | undefined) => ({
  offerLine: lineDecoder(offerLineSpec, currency),
  optionLine: lineDecoder(optionLineSpec, currency),
  categoryLine: lineDecoder(categoryLineSpec, currency),
});

const lineDecoder =
  <T extends ShapedLine>(spec: LineSpec<T>, currency?: string | undefined) =>
  (line: ShapelessLine): Partial<T> => {
    const isField = (fieldName: string): fieldName is FieldOf<T> => (spec.fields as string[]).includes(fieldName);
    const isNotEmpty = (value: any): boolean =>
      value !== undefined && !(typeof value === 'string' && value.length === 0);

    const withCurrency = (spec.fields as string[]).includes('currency') ? { currency } : {};

    const pairs = Object.entries(line)
      .map(([field, value]) => {
        const normalized = field.trim().replace(/\s+/g, ' ').replace(' ', '_').toLowerCase();
        return [normalized, value] as const;
      })
      .filter(([field, value]) => isField(field) && isNotEmpty(value))
      .map(([field, value]) => {
        const trustedField = field as FieldOf<T>; // enforced by filter
        const parsed =
          value && spec.numericFields.includes(trustedField)
            ? parseFloat(value.toString())
            : value && spec.enforceStringFields.includes(trustedField)
              ? value.toString()
              : value && spec.enforceMaxImagesFields?.includes(trustedField)
                ? value.toString().split(',\n', ITEM_MAX_IMAGE_COUNT).join(',\n')
                : value;
        return { [trustedField]: Number.isNaN(parsed) ? undefined : parsed } as Partial<T>;
      });
    const standardLine = pairs.reduce((acc, elem) => ({ ...acc, ...elem }), {});
    return Object.keys(standardLine).length === 0 ? standardLine : { ...standardLine, ...withCurrency };
  };

const unlocalisedString = (values: LocalisedValue[] | undefined, lang: string): string | undefined =>
  (values || []).find(value => value.lang === lang)?.value ?? undefined;

export const discountAmount = (offer: Offer): number | undefined => {
  const discount = offer?.discounted;
  switch (discount?.discount_type) {
    case undefined:
      return undefined;
    case 'absolute':
      return discount.original_price - offer.price;
    case 'relative':
      return Math.round(((discount.original_price - offer.price) * 100) / discount.original_price);
  }
};

const offerOriginalPrice = (offer: Offer): number | undefined => {
  const originalPrice = offer?.discounted?.original_price;
  return originalPrice ?? offer.price;
};

const offerLineSpec: LineSpec<OfferLine> = {
  enforceStringFields: ['gtin', 'id', 'merchant_sku', 'external_id', 'name', 'product_hierarchy_level_id'],
  enforceMaxImagesFields: ['images'],
  numericFields: [
    'price',
    'vat',
    'caffeine_content_value',
    'max_quantity_per_purchase',
    'min_quantity_per_purchase',
    'number_of_units',
    'volume_in_ml',
    'weight_in_grams',
    'discount',
    'sell_by_weight_grams_per_step',
  ],
  booleanFields: ['enabled', 'is_bundle_offer', 'wolt_plus_only'],
  fields: [
    'id',
    'additives',
    'alcohol_percentage',
    'allergens',
    'brand_id',
    'caffeine_content_value',
    'caffeine_content_units',
    'conditions_of_use_and_storage',
    'country_of_origin',
    'currency',
    'external_id',
    'is_bundle_offer',
    'max_quantity_per_purchase',
    'merchant_sku',
    'min_quantity_per_purchase',
    'name',
    'number_of_units',
    'price',
    'product_id',
    'volume_in_ml',
    'weight_in_grams',
    'wolt_plus_only',
    'courier_restrictions',
    'delivery_methods',
    'images',
    'description',
    'dietary_preferences',
    'deposit_id',
    'discount_type',
    'discount',
    'display_unit',
    'distributor_information',
    'enabled',
    // 'after',
    // 'before',
    'gtin',
    'ingredients',
    'inventory_mode',
    'mandatory_warnings',
    'more_information',
    'nutrition_facts',
    'nutrition_values',
    'option_bindings',
    'producer_information',
    'restrictions',
    'sell_by_weight_type',
    'sell_by_weight_grams_per_step',
    'user_requirements',
    'vat',
    'vat_category',
    'product_hierarchy_level_id',
    // 'weekly_availability_start',
    // 'weekly_availability_end',
    // 'weekly_visibility_start',
    // 'weekly_visibility_end',
  ],
};

const optionLineSpec: LineSpec<OptionLine> = {
  booleanFields: ['default', 'visible_to_merchant_app', 'visible_to_courier'],
  enforceStringFields: ['id', 'merchant_sku', 'external_id'],
  numericFields: ['price', 'vat', 'alcohol_permille', 'max', 'min', 'numberOfUnits', 'volume_in_ml', 'weight_in_grams'],
  fields: [
    'id',
    'name',
    'type',
    'additives',
    'alcohol_permille',
    'allergens',
    'conditions_of_use_and_storage',
    'country_of_origin',
    'currency',
    'default',
    'dietary_preferences',
    'deposit_id',
    'description',
    'display_unit',
    'distributor_information',
    'external_id',
    'gtin',
    'ingredients',
    'mandatory_warnings',
    'max',
    'min',
    'merchant_sku',
    'more_information',
    'option_value_id',
    'option_value_name',
    'nutrition_facts',
    'nutrition_values',
    'numberOfUnits',
    'price',
    'producer_information',
    'vat',
    'vat_category',
    'visible_to_courier',
    'visible_to_merchant_app',
    'volume_in_ml',
    'weight_in_grams',
  ],
};

const categoryLineSpec: LineSpec<CategoryLine> = {
  enforceStringFields: ['id'],
  numericFields: [],
  booleanFields: [],
  fields: ['id', 'description', 'offers', 'name', 'subcategories'],
};

export const lineSpecs = {
  options: optionLineSpec,
  offers: offerLineSpec,
  categories: categoryLineSpec,
};
